import {ApiResponse} from 'back-connector'
import {SENDBIRD_CONFIG} from '@/constants/sendbird'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {AI_CHAT_CONSTANTS, AI_CHAT_ERRORS} from '../constants'
import {
    AiChatAuthRequest,
    AiChatAuthResponse,
    CreateUserResponse,
    CreateUserRequest
} from '../models'

export const generateAiChatToken = async (
    userId: string
): Promise<ApiResponse<AiChatAuthResponse>> => {
    const expiresAt = Date.now() + AI_CHAT_CONSTANTS.TOKEN_EXPIRY_MS
    const authUrl = `https://api-${SENDBIRD_CONFIG.APP_ID}.sendbird.com/v3/users/${userId}/token`

    const requestBody: AiChatAuthRequest = {
        expires_at: expiresAt
    }

    try {
        const response = await fetch(authUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                [AI_CHAT_CONSTANTS.AUTH_HEADER]: _SENDBIRD_MASTER_TOKEN_
            },
            body: JSON.stringify(requestBody)
        })

        if (!response.ok) {
            return {
                result: null,
                error: {
                    code: response.status.toString(),
                    message: response.statusText
                }
            }
        }

        const data: AiChatAuthResponse = await response.json()
        return {
            result: data,
            error: null
        }
    } catch (error) {
        captureSentryError({
            name: 'AIChatFetchers generateAiChatToken Error',
            description: getErrorMessage(error, 'Failed to generate AI chat token'),
            fingerprint: ['ai-chat-fetchers-generate-ai-chat-token'],
            error
        })
        return {
            result: null,
            error: {
                code: '500',
                message: error.message
            }
        }
    }
}

export const createSendbirdUser = async (
    userId: string,
    nickname: string
): Promise<ApiResponse<CreateUserResponse>> => {
    const createUserUrl = `https://api-${SENDBIRD_CONFIG.APP_ID}.sendbird.com/v3/users`

    const requestBody: CreateUserRequest = {
        user_id: userId,
        nickname: nickname,
        profile_url: ''
    }

    try {
        const response = await fetch(createUserUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                [AI_CHAT_CONSTANTS.AUTH_HEADER]: _SENDBIRD_MASTER_TOKEN_
            },
            body: JSON.stringify(requestBody)
        })

        if (response.status === 400) {
            const errorData = await response.json()
            if (errorData.code === AI_CHAT_ERRORS.RESOURCE_ALREADY_EXISTS) {
                return {
                    result: {
                        user_id: userId,
                        nickname: nickname,
                        profile_url: '',
                        is_online: false,
                        is_active: true,
                        created_at: Date.now(),
                        last_seen_at: Date.now()
                    },
                    error: null
                }
            }
        }

        if (!response.ok) {
            return {
                result: null,
                error: {
                    code: response.status.toString(),
                    message: response.statusText
                }
            }
        }

        const data: CreateUserResponse = await response.json()
        return {
            result: data,
            error: null
        }
    } catch (error) {
        captureSentryError({
            name: 'AIChatFetchers createSendbirdUser Error',
            description: getErrorMessage(error, 'Failed to create Sendbird user'),
            fingerprint: ['ai-chat-fetchers-create-sendbird-user'],
            error
        })
        return {
            result: null,
            error: {
                code: '500',
                message: error.message
            }
        }
    }
}
